import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useActiveClinic, type ClinicInfo } from '@/lib/ActiveClinicContext';
import { cn } from '@/utils/cn';

export function ClinicSelector() {
  const { activeClinic, availableClinics, setActiveClinicId, hasMultipleClinics } = useActiveClinic();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Don't render if only one clinic
  if (!hasMultipleClinics) return null;

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Group clinics: main clinics first, then sub-clinics under their parent
  const groupedClinics = availableClinics.reduce<{
    main: ClinicInfo[];
    byParent: Record<string, ClinicInfo[]>;
  }>((acc, clinic) => {
    if (!clinic.isSubClinic) {
      acc.main.push(clinic);
    } else if (clinic.parentClinicId) {
      if (!acc.byParent[clinic.parentClinicId]) {
        acc.byParent[clinic.parentClinicId] = [];
      }
      acc.byParent[clinic.parentClinicId].push(clinic);
    }
    return acc;
  }, { main: [], byParent: {} });

  const handleSelect = (clinicId: string) => {
    setActiveClinicId(clinicId);
    setIsOpen(false);
  };

  const getRoleBadge = (role: ClinicInfo['role']) => {
    const badges = {
      main_clinic_admin: { label: 'Main Admin', class: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
      clinic_admin: { label: 'Admin', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
      employee: { label: 'Employee', class: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    };
    return badges[role] || badges.employee;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg",
          "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
          "hover:bg-gray-50 dark:hover:bg-gray-700",
          "focus:outline-none focus:ring-2 focus:ring-indigo-500",
          "transition-colors duration-150"
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Building2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
          {activeClinic?.name ?? 'Select Clinic'}
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 text-gray-400 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={cn(
          "absolute left-0 mt-2 w-72 z-50",
          "bg-white dark:bg-gray-800 rounded-xl shadow-lg",
          "border border-gray-200 dark:border-gray-700",
          "py-1 max-h-80 overflow-y-auto"
        )}
        role="listbox"
        >
          {groupedClinics.main.map((clinic) => (
            <div key={clinic.id}>
              {/* Main Clinic */}
              <ClinicOption
                clinic={clinic}
                isActive={clinic.id === activeClinic?.id}
                onSelect={handleSelect}
                getRoleBadge={getRoleBadge}
              />
              {/* Sub-clinics */}
              {groupedClinics.byParent[clinic.id]?.map((subClinic) => (
                <ClinicOption
                  key={subClinic.id}
                  clinic={subClinic}
                  isActive={subClinic.id === activeClinic?.id}
                  onSelect={handleSelect}
                  getRoleBadge={getRoleBadge}
                  isSubClinic
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ClinicOptionProps {
  clinic: ClinicInfo;
  isActive: boolean;
  onSelect: (id: string) => void;
  getRoleBadge: (role: ClinicInfo['role']) => { label: string; class: string };
  isSubClinic?: boolean;
}

function ClinicOption({ clinic, isActive, onSelect, getRoleBadge, isSubClinic }: ClinicOptionProps) {
  const badge = getRoleBadge(clinic.role);
  
  return (
    <button
      type="button"
      onClick={() => onSelect(clinic.id)}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2 text-left",
        "hover:bg-gray-50 dark:hover:bg-gray-700",
        isActive && "bg-indigo-50 dark:bg-indigo-900/20",
        isSubClinic && "pl-8"
      )}
      role="option"
      aria-selected={isActive}
    >
      {/* Avatar/Initial */}
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold",
        isActive 
          ? "bg-indigo-600 text-white" 
          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
      )}>
        {clinic.name.charAt(0).toUpperCase()}
      </div>
      
      {/* Clinic Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            "text-sm font-medium truncate",
            isActive ? "text-indigo-600 dark:text-indigo-400" : "text-gray-900 dark:text-white"
          )}>
            {clinic.name}
          </span>
          {isSubClinic && (
            <span className="text-xs text-gray-400">(sub)</span>
          )}
          {clinic.isActive === false && (
            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">(Inactive)</span>
          )}
        </div>
        <span className={cn("text-xs px-1.5 py-0.5 rounded", badge.class)}>
          {badge.label}
        </span>
      </div>
      
      {/* Check Mark */}
      {isActive && (
        <Check className="h-4 w-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
      )}
    </button>
  );
}
